import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Printer, Send, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { refertiProdottiAPI } from '../../services/refertiProdottiApi';
import RefertiProcedura from './RefertiProcedura';
import BottoneRefertoDaProcedura from './BottoneRefertoDaProcedura';
import ModuloFoto from './ModuloFoto';
import ModuloParametri from './ModuloParametri';
import ModuloZone from './ModuloZone';
import api, { dettaglioAPI, visiteAPI } from '../../services/api';
import { pacchettiAPI } from '../../services/fatturazioneApi';
import { etichettaPacchetto } from '../../utils/etichettaPacchetto';
import '../../styles/ProceduraDettaglio.css';
import SomministrazioniVisita from './SomministrazioniVisita';
import { DocumentiGenerati, DocumentiCaricati } from './DocumentiProcedura';
import SchedeProcedura from './SchedeProcedura';
import IntegrazioniVisita from './IntegrazioniVisita';
import { Sezione } from './SezioneProcedura';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { useSalvaCampo } from '../../utils/salvaCampo';
import { notificaErrore, notificaAvviso, notificaConferma, messaggioDa } from '../../utils/notifiche';
import { annunciaProcedureCambiate, annunciaAnamnesiCambiata } from '../../utils/annunci';

const fmtData = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

/* «In costruzione» SOLO per quello che non funziona ancora (le
   registrazioni audio/video). Fino al 23 settembre 2026 l'etichetta
   compariva su ogni blocco vuoto - schemi, parametri, fotografie - e un
   blocco che funziona sembrava una funzione non ancora pronta: e' una
   delle ragioni per cui nella prova di Alex le fotografie «non c'erano». */
const Ph = ({ testo, inCostruzione = false }) => (
    <div className="pd-ph">
        {inCostruzione && <span className="pd-ph-badge">in costruzione</span>}
        {testo || 'Sezione non ancora attiva.'}
    </div>
);

/* «2026-03-14» -> «14/03/2026», senza passare da Date (niente fusi). */
const dataBreve = (v) => {
    const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/**
 * CampoVisita — un campo di testo del riquadro «Visita» che SI SALVA DA SE'
 * quando si esce dal campo (regola di Alex del 24 settembre 2026, in
 * CLAUDE.md). Niente pulsante «Salva».
 *
 * Il meccanismo e' quello dell'anamnesi, non una copia: utils/salvaCampo.js
 * (una coda per campo, «non salvato» che non sparisce da solo, nessun nuovo
 * tentativo automatico, niente testo in localStorage). Qui si aggiunge:
 *
 *   - UN CAMPO CHE NON E' CAMBIATO NON SI SALVA: si confronta con l'ultimo
 *     valore arrivato dal server o salvato. Uscire da un campo senza
 *     toccarlo non manda niente, e non nasce nessuna riga inutile nel
 *     registro modifiche (il server fa lo stesso controllo per conto suo);
 *   - accanto al titolo «salvato» quando e' riuscito; se non e' riuscito il
 *     motivo del server e «Riprova», e il testo resta nel campo;
 *   - LA DIAGNOSI puo' far nascere una patologia (migration 234): se il
 *     testo e' uguale a una patologia aperta il server non salva e chiede,
 *     e la domanda resta sotto il campo finche' non si risponde.
 */
const CampoVisita = ({ visitaId, campo, titolo, valoreIniziale, placeholder, hint, classeTitolo = '', onSalvato, children, bloccato = false,
                      salvaCon = null }) => {
    const [val, setVal] = useState(valoreIniziale || '');
    const salvatoUltimo = useRef(valoreIniziale || '');
    const [errore, setErrore] = useState(null);
    const [domanda, setDomanda] = useState(null); // { voce_id, messaggio, testo }
    const [versione, setVersione] = useState(0);
    const dirty = useSporco(val, versione);

    useEffect(() => {
        setVal(valoreIniziale || '');
        salvatoUltimo.current = valoreIniziale || '';
        setVersione((v) => v + 1);
    }, [valoreIniziale]);

    /* Una scrittura. `v` = { testo, extra }: l'extra porta la risposta alla
       domanda sulla patologia. Se la risposta e' una domanda, la scrittura
       NON e' riuscita (il campo resta «non salvato») e la domanda si
       mostra al posto del messaggio d'errore. */
    const scrivi = useCallback(async (c, v) => {
        try {
            /* `salvaCon`: dove scrive un blocco di diagnosi (migration 256);
               senza, il campo della visita. */
            await (salvaCon
                ? salvaCon(c, v.testo, v.extra || {})
                : dettaglioAPI.saveCampo(visitaId, c, v.testo, v.extra || {}));
            salvatoUltimo.current = v.testo;
            setErrore(null);
            setDomanda(null);
        } catch (e) {
            const d = e?.response?.status === 409 ? e.response.data?.data?.domanda : null;
            if (d && d.tipo === 'stessa_patologia') {
                setDomanda({ voce_id: d.voce_id, messaggio: messaggioDa(e), testo: v.testo });
                setErrore(null);
            } else {
                setErrore(messaggioDa(e, 'Non riesco a salvare il campo'));
            }
            throw e;
        }
    }, [visitaId, salvaCon]);

    const dopo = useCallback(() => {
        setVersione((x) => x + 1);
        annunciaProcedureCambiate();
        if (campo === 'diagnosi') annunciaAnamnesiCambiata();
        if (onSalvato) onSalvato();
    }, [campo, onSalvato]);

    const { stato, salva, nonSalvati } = useSalvaCampo(scrivi, dopo);
    useUscitaProtetta(dirty || nonSalvati.length > 0);
    const st = stato[campo];

    const esci = () => {
        /* In sola lettura (non e' il medico della visita, o il riquadro e'
           chiuso) uscendo non parte niente: il server rifiuterebbe comunque. */
        if (bloccato) return;
        if (val === salvatoUltimo.current) return;                 // non e' cambiato
        if (domanda && domanda.testo === val) return;              // si aspetta la risposta
        salva(campo, { testo: val });
    };
    const rispondi = (extra) => salva(campo, { testo: val, extra });

    return (
        <div className="pd-campo" data-salva-da-solo>
            <div className="pd-campo-testa">
                <h4 className={`pd-visita-etichetta ${classeTitolo}`}>{titolo}</h4>
                {st === 'in_corso' && <span className="pd-campo-stato">salvataggio…</span>}
                {st === 'salvato' && <span className="pd-campo-ok">✓ salvato</span>}
            </div>
            {hint && <div className="pd-campo-hint">{hint}</div>}
            {/* Quello che sta fra il titolo e la casella: il testo della
                visita precedente, in sola lettura. */}
            {children}
            <textarea className={`pd-campo-ta ${st === 'non_salvato' && !domanda ? 'pd-campo-ta--rosso' : ''}`}
                      rows="3" value={val} placeholder={bloccato ? '' : placeholder} readOnly={bloccato}
                      onChange={(e) => setVal(e.target.value)} onBlur={esci} />
            {domanda && (
                <div className="pd-domanda" role="alertdialog">
                    <span>{domanda.messaggio}</span>
                    <button type="button" className="pd-campo-save"
                            onClick={() => rispondi({ patologia: { scelta: 'collega', voce_id: domanda.voce_id } })}
                            disabled={st === 'in_corso'}>
                        Collega
                    </button>
                    <button type="button" className="pd-campo-save"
                            onClick={() => rispondi({ patologia: { scelta: 'nuova' } })}
                            disabled={st === 'in_corso'}>
                        È una nuova
                    </button>
                </div>
            )}
            {st === 'non_salvato' && !domanda && (
                <div className="pd-campo-errore" role="alert">
                    <span>Non salvato: {errore || 'il salvataggio non è riuscito'}. Il testo è ancora nel campo.</span>
                    <button type="button" className="pd-campo-riprova" onClick={() => salva(campo, { testo: val })}>
                        Riprova
                    </button>
                </div>
            )}
        </div>
    );
};

/**
 * «Stampa»: il REFERTO DELLA VISITA (fase 2 del riordino). La parte
 * «Visita» sempre, e una parte per ogni prestazione spuntata: cosa entra
 * lo decide il server, in services/pdfProcedura.js.
 */
const BottoneStampaReferto = ({ visitaId }) => {
    const [inCorso, setInCorso] = useState(false);
    const stampa = async () => {
        setInCorso(true);
        try {
            const x = await api.get(`/visite/${visitaId}/stampa-pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([x.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 30000);
        } catch (e) {
            /* La risposta e' un blob anche quando e' un errore: il
               messaggio del server va letto dal blob prima di mostrarlo. */
            let messaggio = 'Non riesco a stampare il referto';
            try {
                const testo = await e?.response?.data?.text?.();
                if (testo) messaggio = JSON.parse(testo).message || messaggio;
            } catch (_) { /* resta il ripiego */ }
            notificaErrore(messaggio);
        } finally {
            setInCorso(false);
        }
    };
    return (
        <button type="button" className="pd-stampa" onClick={stampa} disabled={inCorso}
                title="Il referto della visita: la parte «Visita» e le prestazioni con la spunta «Includi nel referto».">
            <Printer size={15} strokeWidth={1.8} aria-hidden="true" />
            {inCorso ? 'Preparo il referto…' : 'Stampa'}
        </button>
    );
};

/* «Invia al FSE»: un SEGNAPOSTO, deciso da Alex il 23 settembre 2026. Sta
   dove stara' la funzione vera, perche' i medici si abituino a trovarlo
   li'. Premuto non fa niente, e lo dice: un pulsante muto sembrerebbe
   rotto. */
const BottoneFse = () => (
    <span className="pd-fse">
        <button type="button" className="pd-stampa pd-fse-btn"
                onClick={() => notificaAvviso('Invio al Fascicolo Sanitario Elettronico: funzione in attivazione. '
                                            + 'Per ora il referto si stampa.')}>
            <Send size={15} strokeWidth={1.8} aria-hidden="true" /> Invia al FSE
        </button>
        <small className="pd-fse-nota">Funzione in attivazione</small>
    </span>
);

/**
 * «Includi nel referto», sul riquadro INTERO di una prestazione. Il valore
 * di partenza viene dalla voce di catalogo (deciso per tutto lo studio); il
 * medico della visita, o chi ha eseguito la prestazione, lo cambia per
 * questa visita. Gli altri lo vedono e basta.
 */
const IncludiNelReferto = ({ visita, prestazione, utente, onCambiata }) => {
    const [valore, setValore] = useState(!!prestazione.includi_nel_referto);
    const [inCorso, setInCorso] = useState(false);
    useEffect(() => { setValore(!!prestazione.includi_nel_referto); }, [prestazione.includi_nel_referto]);
    const mio = utente?.medico_id ? Number(utente.medico_id) : null;
    const puo = !!mio && (mio === Number(visita.medico_id) || mio === Number(prestazione.medico_id));
    /* La spunta cambia SUBITO, e torna com'era se il server non la
       salva: aspettare la risposta per disegnarla la faceva sembrare un
       clic perso. */
    const cambia = async (e) => {
        const nuovo = e.target.checked;
        setValore(nuovo);
        setInCorso(true);
        try {
            await visiteAPI.includiNelReferto(visita.id, prestazione.id, nuovo);
            onCambiata(prestazione.id, nuovo);
        } catch (err) {
            setValore(!nuovo);
            notificaErrore(messaggioDa(err, 'Non riesco a cambiare la spunta del referto'));
        } finally {
            setInCorso(false);
        }
    };
    return (
        <label className="pd-includi" title={puo ? 'La parte di questa prestazione entra nel referto della visita'
                                                  : 'La cambia il medico della visita o chi ha eseguito la prestazione'}>
            <input type="checkbox" checked={valore} onChange={cambia} disabled={!puo || inCorso} />
            Includi nel referto
        </label>
    );
};

/**
 * Il riquadro di UNA prestazione della visita: finche' non arriva la
 * fase 3 (i parametri di ogni prestazione presi dal catalogo) contiene i
 * moduli di oggi - Zone, i sette parametri, le fotografie - e le
 * registrazioni audio/video come segnaposto.
 */
const RiquadroPrestazione = ({ visita, prestazione, moduli, onAddModulo, onCreaModulo, onReload, sessoPaziente,
                               utente, scrivibile, onIncludiCambiata, onTolta }) => {
    const [togliendo, setTogliendo] = useState(false);
    const suoi = (tipo) => moduli.filter((m) => m.tipo === tipo && Number(m.visita_prestazione_id) === Number(prestazione.id));
    const aggiungi = (tipo, etichetta) => scrivibile && (
        <button className="pd-add" onClick={() => onAddModulo(visita.id, tipo, prestazione.id)} title={`Aggiungi ${etichetta}`}>
            + {etichetta}
        </button>
    );
    const disegna = (tipo, Comp) => suoi(tipo).map((m) => (
        <div key={m.id} className="pd-modulo">
            <Comp modulo={m} visitaId={visita.id} pazienteId={visita.paziente_id}
                  onUpdate={onReload} sessoPaziente={sessoPaziente} />
        </div>
    ));
    const altro = prestazione.medico_id && Number(prestazione.medico_id) !== Number(visita.medico_id);
    /* La seduta di pacchetto collegata (Alex, 28/09/2026): «Seduta 2 di 5».
       Si annulla da qui chi l'ha eseguita (e l'amministrazione e la
       segreteria): torna al credito, la spettanza sparisce, la traccia resta.
       La procedura resta nella visita. */
    const seduta = prestazione.seduta;
    const puoAnnullare = seduta && (['admin', 'segreteria'].includes(utente?.ruolo)
        || Number(utente?.medico_id) === Number(seduta.medico_id));
    const annullaSeduta = async () => {
        const motivo = window.prompt('Motivo dell\'annullamento della seduta (obbligatorio: resta nella traccia)');
        if (motivo === null) return;
        try {
            const r = await pacchettiAPI.annullaSeduta(seduta.id, motivo);
            notificaAvviso(r.data?.message || 'Seduta annullata');
            if (onIncludiCambiata) onIncludiCambiata(prestazione.id, !!prestazione.includi_nel_referto);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco ad annullare la seduta'));
        }
    };
    /* «TOGLI» (Alex, 3/10/2026, risposta 2): il medico della visita il giorno
       stesso, l'amministrazione qualunque giorno col motivo scritto; mai una
       riga gia' fatturata (bozza compresa) o di una seduta di pacchetto. La
       regola la decide il database (togli_prestazione_rifiuto, migration
       298) e il server risponde col perche': qui si sceglie solo a chi
       mostrare il pulsante e se chiedere il motivo. La seduta ha gia' il suo
       «Annulla la seduta», e la riga di una seduta non si toglie. */
    const suaDiOggi = !!utente?.medico_id && Number(utente.medico_id) === Number(visita.medico_id)
        && visita.di_oggi === true;
    const puoTogliere = !seduta && (suaDiOggi || utente?.ruolo === 'admin');
    const togli = async () => {
        let motivo;
        if (suaDiOggi) {
            if (!window.confirm(`Togli «${prestazione.nome_snapshot}» dalla visita?\n\n`
                + 'Sparisce dalla visita e dal referto della visita; i prodotti che aveva scaricato tornano in magazzino. '
                + 'Resta nel registro delle modifiche, con chi l\'ha tolta e quando.')) return;
        } else {
            motivo = window.prompt(`Perché togli «${prestazione.nome_snapshot}» dalla visita? `
                + 'Il motivo è obbligatorio e resta nel registro delle modifiche.');
            if (motivo === null) return;
        }
        setTogliendo(true);
        try {
            const r = await visiteAPI.togliPrestazione(visita.id, prestazione.id, motivo);
            notificaConferma(r.data?.message || 'Prestazione tolta dalla visita.');
            annunciaProcedureCambiate();
            if (onTolta) onTolta(prestazione.id);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a togliere la prestazione'));
        } finally {
            setTogliendo(false);
        }
    };
    return (
        /* APERTO DI PARTENZA, ogni riquadro di prestazione: chiusi, le
           fotografie delle prestazioni prima dell'ultima non si vedevano
           finche' non si apriva il riquadro. */
        <Sezione title={prestazione.nome_snapshot} defaultOpen
                 right={<IncludiNelReferto visita={visita} prestazione={prestazione} utente={utente}
                                           onCambiata={onIncludiCambiata} />}>
            {puoTogliere && (
                <div className="pd-prestazione-azioni">
                    <button type="button" className="pd-prestazione-togli" onClick={togli} disabled={togliendo}
                            data-togli-prestazione={prestazione.id}
                            title="Togli questa prestazione dalla visita">
                        <Trash2 size={13} aria-hidden="true" /> Togli
                    </button>
                </div>
            )}
            {altro && prestazione.esecutore && (
                <p className="pd-esecutore-riga">Eseguita da {prestazione.esecutore}</p>
            )}
            {seduta && (
                <p className="pd-esecutore-riga pd-seduta-pacchetto">
                    Seduta {seduta.numero} di {seduta.di} del pacchetto
                    {puoAnnullare && (
                        <button type="button" className="pd-link-annulla" onClick={annullaSeduta}>
                            Annulla la seduta
                        </button>
                    )}
                </p>
            )}
            <div className="pd-blocco">
                <div className="pd-blocco-testa"><span>Schemi tecnici</span>{aggiungi('zone', 'Zone trattate')}</div>
                {suoi('zone').length === 0
                    ? <Ph testo="Nessuno schema tecnico. Usa + Zone trattate per segnare dove sei intervenuto." />
                    : disegna('zone', ModuloZone)}
            </div>
            <div className="pd-blocco">
                <div className="pd-blocco-testa"><span>Parametri trattamento</span>{aggiungi('parametri', 'Parametri')}</div>
                {suoi('parametri').length === 0
                    ? <Ph testo="Nessun parametro registrato." />
                    : disegna('parametri', ModuloParametri)}
            </div>
            {/* IL RIQUADRO FOTO C'E' IN OGNI PRESTAZIONE, pronto: BEFORE e
                AFTER si vedono subito, senza «+ Foto» e senza matita. La
                galleria (il modulo) nasce al primo caricamento. */}
            <div className="pd-blocco">
                <div className="pd-blocco-testa"><span>Fotografie</span></div>
                {(suoi('foto').length ? suoi('foto') : [null]).map((m) => (
                    <div key={m ? m.id : `nuova-${prestazione.id}`} className="pd-modulo">
                        <ModuloFoto modulo={m || { id: null, titolo: 'Fotografie', ordine: 0,
                                                   contenuto: { foto: [], descrizione: '' } }}
                                    visitaId={visita.id} pazienteId={visita.paziente_id}
                                    onUpdate={onReload} incorporato
                                    creaModulo={() => onCreaModulo(visita.id, 'foto', prestazione.id)} />
                    </div>
                ))}
            </div>
            <div className="pd-blocco">
                <div className="pd-blocco-testa"><span>Registrazioni audio/video</span></div>
                <Ph inCostruzione />
            </div>
        </Sezione>
    );
};

/**
 * UN BLOCCO «DIAGNOSI + NOTE ANAMNESTICHE» (proposta 17, 4a; migration 256).
 * Una visita ne ha quanti servono, e ognuno e' la sua patologia: la
 * domanda «È la stessa patologia del …?» si fa blocco per blocco. I due
 * campi si salvano da soli come gli altri del riquadro, con le stesse
 * regole (il medico della visita, fino alle 23:59 del giorno dopo).
 *
 * Una visita senza diagnosi mostra un blocco vuoto che non esiste ancora:
 * nasce al primo salvataggio, non all'apertura (un campo che nessuno ha
 * toccato non lascia righe).
 */
const BloccoDiagnosi = ({ visitaId, blocco, numero, totale, bloccato, onCreato, onSalvato, onTolto }) => {
    const idOra = useRef(blocco.id || null);
    const { chiave } = blocco;
    const creazione = useRef(null);
    useEffect(() => { idOra.current = blocco.id || idOra.current; }, [blocco.id]);

    const salvaCon = useCallback(async (campo, testo, extra) => {
        if (!idOra.current) {
            if (!creazione.current) {
                creazione.current = dettaglioAPI.nuovaDiagnosi(visitaId)
                    .then((r) => {
                        const id = r.data.data.diagnosi.id;
                        idOra.current = id;
                        onCreato(chiave, id);
                        return id;
                    })
                    .catch((e) => { creazione.current = null; throw e; });
            }
            await creazione.current;
        }
        return dettaglioAPI.salvaDiagnosi(idOra.current, campo, testo, extra);
    }, [visitaId, onCreato, chiave]);

    const [togliendo, setTogliendo] = useState(false);
    const togli = async () => {
        if (!window.confirm('Togli questa diagnosi? Resta nella storia della visita, con chi l\'ha tolta e quando.')) return;
        setTogliendo(true);
        try {
            const r = await dettaglioAPI.togliDiagnosi(idOra.current);
            notificaConferma(r.data?.message || 'Diagnosi tolta.');
            if (r.data?.data?.patologiaNelCestino) annunciaAnamnesiCambiata();
            annunciaProcedureCambiate();
            onTolto(blocco);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a togliere la diagnosi'));
        } finally {
            setTogliendo(false);
        }
    };

    const titolo = totale > 1 ? `Diagnosi ${numero}` : 'Diagnosi';
    return (
        <div className="pd-diagnosi-blocco" data-diagnosi={numero}>
            <div className="pd-visita-campo">
                <CampoVisita bloccato={bloccato} visitaId={visitaId} campo="diagnosi" titolo={titolo}
                    classeTitolo="pd-visita-diagnosi" salvaCon={salvaCon}
                    valoreIniziale={blocco.diagnosi} placeholder="Diagnosi…"
                    onSalvato={onSalvato}>
                    {/* Sopra la casella e non sotto: fra la casella e il
                        titolo che segue lo spazio resta quello di tutti gli
                        altri campi. */}
                    {blocco.patologia && (
                        <p className="pd-patologia">
                            Patologia: «{blocco.patologia.testo}»
                            {blocco.patologia.dal ? ` (dal ${dataBreve(blocco.patologia.dal)})` : ''}
                        </p>
                    )}
                </CampoVisita>
            </div>
            <div className="pd-visita-campo">
                <CampoVisita bloccato={bloccato} visitaId={visitaId} campo="note_anamnestiche"
                    titolo="Note anamnestiche / Patologie" salvaCon={salvaCon}
                    valoreIniziale={blocco.note_anamnestiche} placeholder="Anamnesi, patologie, allergie rilevanti…">
                    {/* Il testo della visita precedente della stessa
                        patologia: in sola lettura, con la sua data e il suo
                        medico. Non e' copiato: si legge dalla visita dove e'
                        stato scritto, e solo se chi guarda puo' aprirla. */}
                    {blocco.precedente && (
                        <div className="pd-precedente">
                            <div className="pd-precedente-testa">
                                Visita precedente del {dataBreve(blocco.precedente.data)}
                                {blocco.precedente.medico ? ` · ${blocco.precedente.medico}` : ''}
                            </div>
                            <div className="pd-precedente-testo">
                                {blocco.precedente.note || <em>Nessuna nota anamnestica scritta.</em>}
                            </div>
                        </div>
                    )}
                </CampoVisita>
            </div>
            {!bloccato && blocco.id && (
                <button type="button" className="pd-diagnosi-togli" onClick={togli} disabled={togliendo}
                        title="Togli questa diagnosi, scritta per sbaglio">
                    <Trash2 size={13} aria-hidden="true" /> Togli questa diagnosi
                </button>
            )}
        </div>
    );
};

/**
 * Le ricette della visita sotto «Terapia» (proposta 17, 4c; decisione 4b:
 * anche le bozze, segnate). Titolo e data, cliccabili: aprono la ricetta.
 * Chi vede cosa lo decide il server, come nell'elenco dei referti.
 */
const RicetteDellaVisita = ({ visita, versione }) => {
    const [ricette, setRicette] = useState([]);
    useEffect(() => {
        let vivo = true;
        refertiProdottiAPI.elenco(visita.paziente_id, undefined, { visita_id: visita.id, tipo: 'ricetta' })
            .then((r) => { if (vivo) setRicette(r.data?.data?.referti || []); })
            .catch(() => { if (vivo) setRicette([]); });
        return () => { vivo = false; };
    }, [visita.id, visita.paziente_id, versione]);
    if (!ricette.length) return null;
    return (
        <ul className="pd-ricette">
            {ricette.map((r) => (
                <li key={r.id}>
                    <Link to={`/referti/${r.id}`}>{r.titolo}</Link>
                    <span className="pd-ricette-data">{dataBreve(r.data_referto)}</span>
                    {r.stato === 'bozza' && <span className="pd-ricette-bozza">bozza</span>}
                </li>
            ))}
        </ul>
    );
};

const ProceduraDettaglio = ({ visita, moduli, onAddModulo, onCreaModulo, onReload, sessoPaziente,
                              utente, puoAggiungere = false, onNuovaProcedura, onPrestazioniCambiate,
                              pacchettiAperti = [], onContinuaPacchetto }) => {
    const [dati, setDati] = useState({});
    /* Piu' pacchetti aperti: il clic su «Continua pacchetto» mostra l'elenco. */
    const [sceltaPacchetto, setSceltaPacchetto] = useState(false);
    /* Le diagnosi della visita, un blocco per diagnosi (migration 256),
       ognuna con la patologia che segue e il testo della visita
       precedente, letti con i permessi di chi guarda. `chiave` resta
       quella della prima comparsa: un blocco vuoto che nasce al primo
       salvataggio non si ridisegna (perderebbe il campo in scrittura). */
    const [blocchi, setBlocchi] = useState([]);
    /* Chi puo' scrivere nel riquadro e fino a quando: lo dice il server
       (services/riquadroVisita.js). Finche' non ha risposto, sola lettura. */
    const [statoRiq, setStatoRiq] = useState(null);
    /* Le spunte cambiate in questa sessione, per scrivere subito cosa
       entrera' nel referto senza ricaricare tutta la visita. */
    const [inclusi, setInclusi] = useState({});

    const vuoto = () => ({ chiave: `nuova-${Date.now()}`, id: null, diagnosi: '', note_anamnestiche: '',
                           patologia: null, precedente: null });
    const leggiBlocchi = (r) => {
        const elenco = (r.data.data.diagnosi || []).map((b) => ({ ...b, chiave: `b${b.id}` }));
        setBlocchi(elenco.length ? elenco : [vuoto()]);
        setStatoRiq(r.data.data.riquadro || null);
    };
    /* Dopo un salvataggio si rileggono SOLO i legami: rileggere anche i
       testi riporterebbe indietro quelli che il medico sta scrivendo. */
    const leggiLegame = (r) => {
        const perId = {};
        (r.data.data.diagnosi || []).forEach((b) => { perId[b.id] = b; });
        setBlocchi((prima) => prima.map((b) => (b.id && perId[b.id]
            ? { ...b, patologia: perId[b.id].patologia, precedente: perId[b.id].precedente } : b)));
        setStatoRiq(r.data.data.riquadro || null);
    };
    const creato = useCallback((chiave, id) => {
        setBlocchi((prima) => prima.map((b) => (b.chiave === chiave ? { ...b, id } : b)));
    }, []);
    const [aggiungendo, setAggiungendo] = useState(false);
    const aggiungiDiagnosi = async () => {
        setAggiungendo(true);
        try {
            const r = await dettaglioAPI.nuovaDiagnosi(visita.id);
            const b = r.data.data.diagnosi;
            setBlocchi((prima) => [...prima, { ...b, chiave: `b${b.id}` }]);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco ad aggiungere la diagnosi'));
        } finally {
            setAggiungendo(false);
        }
    };
    const tolto = (blocco) => setBlocchi((prima) => {
        const resto = prima.filter((b) => b.chiave !== blocco.chiave);
        return resto.length ? resto : [vuoto()];
    });
    const bloccato = !statoRiq?.scrivibile;
    useEffect(() => {
        let alive = true;
        dettaglioAPI.get(visita.id)
            .then(r => {
                if (!alive || !r.data.success) return;
                setDati(r.data.data.dati || {});
                leggiBlocchi(r);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [visita.id]);
    /* Dopo il salvataggio della diagnosi si rilegge SOLO il legame con la
       patologia: rileggere anche i campi riporterebbe indietro quelli che
       il medico sta scrivendo e non ha ancora salvato. */
    const [giro, setGiro] = useState(0);
    useEffect(() => {
        if (!giro) return undefined;
        let alive = true;
        dettaglioAPI.get(visita.id)
            .then(r => { if (alive && r.data.success) leggiLegame(r); })
            .catch(() => {});
        return () => { alive = false; };
        /* eslint-disable-next-line */
    }, [giro]);

    useEffect(() => { setInclusi({}); }, [visita.id, visita.prestazioni]);

    const prestazioni = visita.prestazioni || [];
    const inclusa = (p) => (p.id in inclusi ? inclusi[p.id] : !!p.includi_nel_referto);
    const nelReferto = prestazioni.filter(inclusa).map((p) => p.nome_snapshot);
    /* Chi puo' scrivere nei moduli e nei testi lo decide il server
       (puoVedereDettaglio), come prima della fase 2: qui non si toglie a
       nessuno. Cambia solo chi REGISTRA una prestazione - un medico - e
       quello sta nel pulsante «Nuova procedura» e nel server. */
    const scrivibile = true;

    /* I moduli senza prestazione: quelli scritti prima della fase 2, o di
       una prestazione tolta. Restano visibili, nella parte della visita. */
    const senzaPrestazione = (moduli || []).filter((m) => !m.visita_prestazione_id);
    const legacyDi = (tipo) => senzaPrestazione.filter((m) => m.tipo === tipo);
    const disegnaLegacy = (tipo, Comp) => legacyDi(tipo).map((m) => (
        <div key={m.id} className="pd-modulo">
            <Comp modulo={m} visitaId={visita.id} pazienteId={visita.paziente_id}
                  onUpdate={onReload} sessoPaziente={sessoPaziente} />
        </div>
    ));

    return (
        <div className="pd-wrap">
            <div className="pd-cols">
                {/* ---------- COLONNA SINISTRA ---------- */}
                <div className="pd-col">
                    {/* «VISITA»: i tre testi della visita in un riquadro solo
                        (fase 1 del riordino). Ogni campo si salva da se',
                        sulla stessa rotta e con lo stesso nome di prima. */}
                    <Sezione title="Visita" defaultOpen>
                        {/* LE DIAGNOSI (proposta 17, 4a; migration 256): un blocco
                            «diagnosi + note anamnestiche» per diagnosi, quanti
                            ne servono. La «Patologia registrata» della visita
                            (la colonna di prima, un'altra cosa) resta sopra. */}
                        {visita.patologia && (
                            <div className="pd-campo-hint">Patologia registrata: {visita.patologia}</div>
                        )}
                        {blocchi.map((b, i) => (
                            <BloccoDiagnosi key={b.chiave} visitaId={visita.id} blocco={b}
                                numero={i + 1} totale={blocchi.length} bloccato={bloccato}
                                onCreato={creato} onSalvato={() => setGiro((g) => g + 1)}
                                onTolto={tolto} />
                        ))}
                        {!bloccato && (
                            <button type="button" className="pd-diagnosi-aggiungi" onClick={aggiungiDiagnosi}
                                    disabled={aggiungendo}>
                                <Plus size={14} aria-hidden="true" /> Aggiungi diagnosi
                            </button>
                        )}
                        <div className="pd-visita-campo">
                            <CampoVisita bloccato={bloccato} visitaId={visita.id} campo="esame_obiettivo" titolo="Esame obiettivo"
                                valoreIniziale={dati.esame_obiettivo}
                                placeholder="Esame obiettivo…" />
                        </div>
                        <div className="pd-visita-campo">
                            {/* «TERAPIA» (proposta 17, 4b): si chiamava «Finalità
                                terapeutiche». Solo l'etichetta: la chiave resta
                                finalita_terapeutiche, e non c'entra con
                                l'attestazione di finalità terapeutica. Sopra il
                                testo libero, le ricette della visita. */}
                            <CampoVisita bloccato={bloccato} visitaId={visita.id} campo="finalita_terapeutiche" titolo="Terapia"
                                valoreIniziale={dati.finalita_terapeutiche} placeholder="Terapia e indicazioni…">
                                <RicetteDellaVisita visita={visita} versione={giro} />
                            </CampoVisita>
                        </div>
                        {/* CHI SCRIVE E FINO A QUANDO (Alex, 24/09/2026): il
                            medico della visita, fino alle 23:59 del giorno dopo.
                            Dopo, e per tutti gli altri, sola lettura; le
                            correzioni sono integrazioni, qui sotto. */}
                        {statoRiq && !statoRiq.scrivibile && (
                            <div className="pd-riquadro-nota">
                                {statoRiq.medico
                                    ? `Il riquadro si modificava fino alle 23:59 del ${statoRiq.fino}: adesso è chiuso. Per correggere o aggiungere, scrivi un'integrazione.`
                                    : 'Il riquadro «Visita» lo scrive solo il medico della visita.'}
                            </div>
                        )}
                        <IntegrazioniVisita visitaId={visita.id} />
                    </Sezione>

                    {/* «NUOVA PROCEDURA» STA QUI, sotto la visita (fase 2): una
                        prestazione in piu' nella visita di oggi, col suo
                        riquadro. Solo a un medico, solo oggi. */}
                    {puoAggiungere && (
                        <div className="pd-azioni-procedura">
                            <button type="button" className="pd-nuova-procedura" onClick={onNuovaProcedura}>
                                <Plus size={15} aria-hidden="true" /> Nuova procedura
                            </button>
                            {/* «CONTINUA PACCHETTO» (Alex, 28/09/2026): solo se il
                                paziente ha un pacchetto che questo medico puo'
                                continuare - sedute residue, non scaduto, voce che
                                esegue. Apre la stessa finestra di «Nuova procedura». */}
                            {pacchettiAperti.length > 0 && onContinuaPacchetto && (
                                <button type="button" className="pd-nuova-procedura"
                                        onClick={() => (pacchettiAperti.length === 1
                                            ? onContinuaPacchetto(pacchettiAperti[0])
                                            : setSceltaPacchetto((x) => !x))}>
                                    <Plus size={15} aria-hidden="true" /> Continua pacchetto
                                </button>
                            )}
                        </div>
                    )}
                    {puoAggiungere && sceltaPacchetto && pacchettiAperti.length > 1 && (
                        <ul className="pd-scelta-pacchetto">
                            {pacchettiAperti.map((p) => (
                                <li key={p.id}>
                                    <button type="button" onClick={() => { setSceltaPacchetto(false); onContinuaPacchetto(p); }}>
                                        {etichettaPacchetto(p)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Un riquadro per ogni prestazione della visita, tutti
                        aperti: ognuno ha i suoi schemi, parametri e foto. */}
                    {prestazioni.map((p) => (
                        <RiquadroPrestazione key={p.id} visita={visita} prestazione={p}
                                             moduli={moduli || []} onAddModulo={onAddModulo}
                                             onCreaModulo={onCreaModulo} onReload={onReload}
                                             sessoPaziente={sessoPaziente} utente={utente}
                                             scrivibile={scrivibile}
                                             onIncludiCambiata={(id, v) => {
                                                 setInclusi((x) => ({ ...x, [id]: v }));
                                                 if (onPrestazioniCambiate) onPrestazioniCambiate();
                                             }}
                                             onTolta={() => {
                                                 /* I moduli della prestazione tolta restano della
                                                    visita: si rileggono, e si vedono qui sotto. */
                                                 if (onReload) onReload();
                                                 if (onPrestazioniCambiate) onPrestazioniCambiate();
                                             }} />
                    ))}

                    {senzaPrestazione.length > 0 && (
                        <Sezione title="Schemi e parametri della visita" defaultOpen={!prestazioni.length}>
                            <p className="pd-esecutore-riga">
                                Registrati prima che ogni prestazione avesse il suo riquadro, o di una prestazione tolta: restano della visita.
                            </p>
                            {legacyDi('zone').length > 0 && disegnaLegacy('zone', ModuloZone)}
                            {legacyDi('parametri').length > 0 && disegnaLegacy('parametri', ModuloParametri)}
                            {legacyDi('foto').length > 0 && disegnaLegacy('foto', ModuloFoto)}
                        </Sezione>
                    )}

                    {/* Le schede del medico di questa visita: la fase 3 le
                        carichera' per prestazione. Non disegna niente se il
                        medico non ha schede attive. */}
                    <SchedeProcedura visita={visita} />
                </div>

                {/* ---------- COLONNA DESTRA ---------- */}
                <div className="pd-col">
                    {/* Referti e ricette scritti nell'editor: documenti a se',
                        diversi dal referto della visita qui sotto. */}
                    <Sezione title="Referti / Prescrizioni" defaultOpen>
                        <RefertiProcedura visita={visita}
                                          azioni={<BottoneRefertoDaProcedura visitaId={visita.id} />} />
                    </Sezione>

                    <Sezione title="Appuntamento" defaultOpen>
                        <div className="pd-appt">
                            <div className="pd-appt-data">{fmtData(visita.data_visita)}</div>
                            {visita.trattamento && <div className="pd-appt-tratt">{visita.trattamento}</div>}
                            {visita.medico_cognome && (
                                <div className="pd-appt-medico">Dr. {visita.medico_cognome} {visita.medico_nome}</div>
                            )}
                            {visita.patologia && <div className="pd-appt-pat">Diagnosi: {visita.patologia}</div>}
                            {visita.note_generali && <div className="pd-appt-pat">Note: {visita.note_generali}</div>}
                        </div>
                    </Sezione>

                    <Sezione title="Documenti generati / genera documenti" defaultOpen>
                        <DocumentiGenerati visita={visita} />
                    </Sezione>

                    <Sezione title="Documenti caricati / carica documenti" defaultOpen>
                        <DocumentiCaricati visita={visita} />
                    </Sezione>

                    <Sezione icon="📦" title="Prodotti scaricati da magazzino">
                        <SomministrazioniVisita visitaId={visita?.id} pazienteId={visita?.paziente_id} />
                    </Sezione>
                </div>
            </div>

            {/* ---------- IL REFERTO DELLA VISITA ----------
                In fondo, dove si arriva dopo aver scritto tutto. Il campo
                libero facoltativo (chiesto da Alex il 21 settembre) si stampa
                in coda. La barra con «Stampa» e «Invia al FSE» resta attaccata
                al fondo dello schermo finche' la visita e' aperta: e' quello
                che Alex non trovava, e deve vedersi senza scorrere. */}
            <div className="pd-referto-visita">
                <CampoVisita bloccato={bloccato} visitaId={visita.id} campo="note_referto" titolo="Testo libero del referto (facoltativo)"
                             valoreIniziale={dati.note_referto}
                             placeholder="Si stampa in fondo al referto: indicazioni, conclusioni, quello che vuoi aggiungere…" />
            </div>
            <div className="pd-piede pd-piede--fisso">
                <span className="pd-nel-referto">
                    Nel referto: <strong>Visita</strong>
                    {nelReferto.map((n) => <span key={n}>, {n}</span>)}
                </span>
                <BottoneFse />
                <BottoneStampaReferto visitaId={visita.id} />
            </div>
        </div>
    );
};

export default ProceduraDettaglio;
